import React, { useCallback, useImperativeHandle, useState } from "react";
import type { Ref } from "react";
import { isFunction } from "@common/helpers/typeGuards";
import { OAuthServiceType } from "@common/constants";
import { useWindowMessage } from "@hooks/useWindowMessage";
import { useInteraction } from "@hooks/useInteraction";
import { useEntities } from "@hooks/useEntities";
import { OAuthAuthorizationResponse, triggerAuthorization } from "@lib/oauth";
import { useRampConnectionCreationMutation } from "@mutations/rampConnection";
import {
    ConnectionActionButton,
    ConnectionButtonSize,
    ConnectionButtonVariant,
} from "@components/financialConnection/ConnectionActionButton";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import {
    AddAccountProps,
    CreateAccountHandle,
} from "../accounts/AddAccounts/lib";

interface Props extends AddAccountProps {
    children?:
        | React.ReactNode
        | ((props: {
              connecting: boolean;
              initializeConnection: () => void;
          }) => React.ReactNode);
    btnVariant?: ConnectionButtonVariant;
    btnSize?: ConnectionButtonSize;
    toastMessage?: string;
}
export const CreateRampConnection = ({
    children = <>Connect</>,
    onCreated,
    defaultEntity: defaultEntityProp,
    btnVariant = "secondary",
    btnSize = "sm",
    toastMessage,
    ref,
}: Props & { ref?: Ref<CreateAccountHandle> }) => {
    const mutation = useRampConnectionCreationMutation({
        onSuccess: onCreated,
        toastMessage,
    });
    const [creating, setCreating] = useState(false);

    const businessEntities = useEntities({
        onlyBusiness: true,
        excludeMockEntities: true,
    });
    const defaultEntity =
        defaultEntityProp ??
        (businessEntities.length === 1 ? businessEntities[0] : undefined);

    const handler = ({ code }: OAuthAuthorizationResponse) => {
        if (creating) {
            mutation.mutate({ code, defaultEntity });
        }
        setCreating(false);
    };

    useWindowMessage<OAuthAuthorizationResponse>({
        scope: OAuthServiceType.RAMP,
        handler,
    });

    const { refuseIfViewOnly } = useViewOnlyRefusal();

    const initializeConnection = useCallback(() => {
        if (creating) {
            return;
        }

        // The next step leaves the app, so the server never gets to refuse
        // it. Refuse here instead of after the provider's consent screen.
        if (refuseIfViewOnly()) {
            return;
        }

        triggerAuthorization(OAuthServiceType.RAMP);
        setCreating(true);
    }, [creating, refuseIfViewOnly]);

    const authorize = useInteraction(initializeConnection);

    useImperativeHandle(
        ref,
        () => ({
            initializeConnection,
        }),
        [initializeConnection],
    );

    if (isFunction(children)) {
        return children({ connecting: creating, initializeConnection });
    }

    return (
        <span {...authorize}>
            <ConnectionActionButton
                loading={mutation.isPending}
                variant={btnVariant}
                size={btnSize}
            >
                {children}
            </ConnectionActionButton>
        </span>
    );
};
